import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
// @ts-ignore
const JsBarcode = require('jsbarcode');

export interface SparePartForLabel {
  sparePartId: number;
  sparePartName: string;
  sparePartNameEs?: string; // Nombre en español para etiquetas
  productCode: string;
  packageNumber?: string;
}

export interface GenerateLabelsAndPackageRequest {
  orderId: number;
  selectedSpareParts: SparePartForLabel[];
  generatedBy?: string;
}

export interface SparePartLabelInfo {
  sparePartId: number;
  sparePartName: string;
  sparePartNameEs?: string; // Nombre en español para etiquetas
  productCode: string;
  packageNumber: string;
  orderNumber: string;
  siniestro: string;
  clienteUY: string;
  positionInPackage?: number; // Posición del repuesto en el paquete
  totalInPackage?: number; // Total de repuestos en el paquete
  // Información del cliente
  clientName?: string;
  clientPhone?: string;
  deliveryAddress?: string;
  // Información del taller asignado
  workshopName?: string;
  workshopAddress?: string;
  workshopPhone?: string;
  workshopCity?: string;
  workshopDepartment?: string;
  // Información del proveedor
  providerPhone?: string;
}

export interface GenerateLabelsAndPackageResponse {
  success: boolean;
  message: string;
  packageListId: number | null;
  labels: SparePartLabelInfo[];
  totalSparePartsProcessed: number;
  emergentCommentAlerts?: EmergentCommentAlertGroup[];
}

export interface EmergentCommentAlertGroup {
  comment: string;
  items: EmergentCommentAlertItem[];
}

export interface EmergentCommentAlertItem {
  comment: string;
  sparePartId: number;
  sparePartName: string;
  productCode: string;
  packageNumber?: string;
  orderNumber?: string;
  triggerFields: string[];
}

export interface AuthorizeLabelPrintRequest {
  sparePartIds: number[];
  pin?: string;
}

export interface AuthorizeLabelPrintResponse {
  authorized: boolean;
  requiresPin: boolean;
  message: string;
  firstPrintSparePartIds: number[];
  reprintSparePartIds: number[];
}

export interface RecordLabelPrintRequest {
  sparePartIds: number[];
}

export interface RecordLabelPrintResponse {
  success: boolean;
  message: string;
  recordedCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class LabelsService {

  private apiUrl = environment.apiEndpoint;

  constructor(private http: HttpClient) { }

  /**
   * Genera etiquetas y empaqueta automáticamente una orden
   */
  generateLabelsAndPackage(request: GenerateLabelsAndPackageRequest): Observable<GenerateLabelsAndPackageResponse> {
    return this.http.post<GenerateLabelsAndPackageResponse>(
      `${this.apiUrl}/Order/generate-labels-and-package`,
      request
    );
  }

  authorizeLabelPrint(request: AuthorizeLabelPrintRequest): Observable<AuthorizeLabelPrintResponse> {
    return this.http.post<AuthorizeLabelPrintResponse>(
      `${this.apiUrl}/LabelPrint/authorize`,
      request
    );
  }

  recordLabelPrint(request: RecordLabelPrintRequest): Observable<RecordLabelPrintResponse> {
    return this.http.post<RecordLabelPrintResponse>(
      `${this.apiUrl}/LabelPrint/record`,
      request
    );
  }

  /**
   * Genera el HTML para imprimir las etiquetas
   */
  generateLabelsHtml(order: any, labels: SparePartLabelInfo[], useSharedNumber: boolean = false, includePackageLabel: boolean = true, explicitWorkshopId?: number): string {
    let html = '';

    // Determinar el taller correcto usando lógica inteligente
    const finalWorkshopId = this.determineCorrectWorkshopId(order, explicitWorkshopId);

    // Crear una copia del order con el workshopId correcto
    const orderWithCorrectWorkshop = {
      ...order,
      workshopId: finalWorkshopId
    };

    // Generar etiqueta del paquete primero (si hay etiquetas y está habilitado)
    if (labels.length >= 1 && includePackageLabel) {
      const packageLabel = this.generatePackageLabelHtml(orderWithCorrectWorkshop, labels[0], labels.length, labels, finalWorkshopId);
      html += packageLabel;
    }

    // Generar etiquetas individuales de repuestos
    labels.forEach((label, index) => {
      const totalLabels = useSharedNumber ? labels.length : 1;
      const currentLabelNumber = useSharedNumber ? index + 1 : 1;
      const logoSrc = this.getCompanyLogoPath(order?.clienteUY);

      // Calcular posición en el paquete
      const positionInPackage = label.positionInPackage || (index + 1);
      const totalInPackage = label.totalInPackage || labels.length;

      html += `
        <div class="label">
          <div class="barcode-container">
            ${this.generateBarcodeSvg(label.packageNumber)}
            <div style="text-align: center; font-size: 11px; font-weight: bold; margin-top: 1px; color: #000;">
              <strong>${label.packageNumber}</strong>
            </div>
          </div>
          <div class="label-info">
            <div><strong>Siniestro:</strong> ${label.siniestro}</div>
            <div><strong>Orden:</strong> ${label.orderNumber}</div>
            <div><strong>Nombre de pieza:</strong> ${label.sparePartNameEs || label.sparePartName || 'N/A'}</div>
            <div><strong>Código de pieza:</strong> ${label.productCode}</div>
          </div>
          <div class="label-footer" style="display: flex; justify-content: space-between; align-items: flex-end; margin-top: auto;">
            <div class="label-number" style="flex: 1;">Número de Pieza: ${positionInPackage}/${totalInPackage}</div>
            <img class="label-logo" src="${logoSrc}" style="max-height: 35px; max-width: 80px; object-fit: contain;" onerror="this.style.display='none'" alt="Logo" />
          </div>
        </div>
      `;
    });

    return html;
  }

  /**
   * Genera y abre ventana de PREVIEW solo para la etiqueta del paquete (versión optimizada para ticketeras)
   * NO imprime automáticamente - útil para testear sin gastar papel
   */
  previewPackageLabelWindowThermal(order: any, packageInfo: any, totalPieces: number, spareParts?: any[], printerWidth: '58mm' | '80mm' | '100mm' = '100mm'): void {
    // Crear etiqueta básica para ticketera usando datos directos de la orden
    const packageLabel: SparePartLabelInfo = {
      sparePartId: 0,
      sparePartName: 'PAQUETE',
      productCode: packageInfo.packageNumber,
      packageNumber: packageInfo.packageNumber,
      orderNumber: packageInfo.orderNumber,
      siniestro: order?.siniestro || 'N/A',
      clienteUY: order?.clienteUY || 'AUTOCOMERCIAL',
      positionInPackage: 1,
      totalInPackage: totalPieces,
      clientName: order?.clientName || '',
      clientPhone: order?.clientPhone || '',
      deliveryAddress: order?.deliveryAddress || '',
      // Usar datos del taller que vienen directamente de la orden (ya incluidos por el componente)
      workshopName: order?.workshopName || 'Sin taller asignado',
      workshopAddress: order?.workshopAddress || '',
      workshopPhone: order?.workshopPhone || '',
      workshopCity: order?.workshopCity || '',
      workshopDepartment: order?.workshopDepartment || '',
      providerPhone: this.getCompanyWhatsApp(order?.clienteUY)
    };

    const thermalHtml = this.generateThermalPackageLabelHtml(packageLabel, spareParts, printerWidth);

    // Calcular dimensiones de la ventana según el tamaño de la etiqueta
    let windowWidth = 450;
    let windowHeight = 700;
    if (printerWidth === '100mm') {
      windowWidth = 500;
      windowHeight = 700;
    }

    const previewWindow = window.open('', '_blank', `width=${windowWidth},height=${windowHeight}`);
    if (previewWindow) {
      // Agregar botón de impresión y regla visual
      const previewHtml = thermalHtml.replace(
        '<body>',
        `<body>
          <div style="position: fixed; top: 10px; right: 10px; z-index: 9999; background: #fff; padding: 10px; border: 2px solid #000; border-radius: 5px;">
            <button onclick="window.print()" style="padding: 10px 20px; font-size: 16px; cursor: pointer; background: #007bff; color: white; border: none; border-radius: 5px; margin-right: 10px;">
              🖨️ Imprimir
            </button>
            <button onclick="window.close()" style="padding: 10px 20px; font-size: 16px; cursor: pointer; background: #dc3545; color: white; border: none; border-radius: 5px;">
              ❌ Cerrar
            </button>
            <div style="margin-top: 10px; font-size: 12px; color: #666;">
              Dimensiones: ${printerWidth === '100mm' ? '100mm x 150mm' : printerWidth === '80mm' ? '80mm x 100mm' : '58mm x 100mm'}
            </div>
          </div>
        `
      );
      previewWindow.document.write(previewHtml);
      previewWindow.document.close();
      previewWindow.focus();
    }
  }

  /**
   * Genera y abre ventana de impresión solo para la etiqueta del paquete (versión optimizada para ticketeras)
   */
  printPackageLabelWindowThermal(order: any, packageInfo: any, totalPieces: number, spareParts?: any[], printerWidth: '58mm' | '80mm' | '100mm' = '100mm'): void {
    // Crear etiqueta básica para ticketera usando datos directos de la orden
    const packageLabel: SparePartLabelInfo = {
      sparePartId: 0,
      sparePartName: 'PAQUETE',
      productCode: packageInfo.packageNumber,
      packageNumber: packageInfo.packageNumber,
      orderNumber: packageInfo.orderNumber,
      siniestro: order?.siniestro || 'N/A',
      clienteUY: order?.clienteUY || 'AUTOCOMERCIAL',
      positionInPackage: 1,
      totalInPackage: totalPieces,
      clientName: order?.clientName || '',
      clientPhone: order?.clientPhone || '',
      deliveryAddress: order?.deliveryAddress || '',
      // Usar datos del taller que vienen directamente de la orden (ya incluidos por el componente)
      // NO usar 'Sin taller asignado' aquí - usar datos reales o vacío
      workshopName: order?.workshopName || '',
      workshopAddress: order?.workshopAddress || '',
      workshopPhone: order?.workshopPhone || '',
      workshopCity: order?.workshopCity || '',
      workshopDepartment: order?.workshopDepartment || '',
      providerPhone: this.getCompanyWhatsApp(order?.clienteUY)
    };

    const thermalHtml = this.generateThermalPackageLabelHtml(packageLabel, spareParts, printerWidth);

    const printWindow = window.open('', '_blank', 'width=400,height=600');
    if (printWindow) {
      printWindow.document.write(thermalHtml);
      printWindow.document.close();
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
      }, 500);
    }
  }

  /**
   * Genera y abre ventana de impresión solo para la etiqueta del paquete
   */
  printPackageLabelWindow(order: any, packageInfo: any, totalPieces: number, spareParts?: any[]): void {
    // Crear un SparePartLabelInfo temporal para la etiqueta del paquete usando datos directos de la orden
    const packageLabel: SparePartLabelInfo = {
      sparePartId: 0,
      sparePartName: 'ETIQUETA DEL PAQUETE',
      productCode: packageInfo.packageNumber,
      packageNumber: packageInfo.packageNumber,
      orderNumber: packageInfo.orderNumber,
      siniestro: order?.siniestro || 'N/A',
      clienteUY: order?.clienteUY || 'AUTOCOMERCIAL',
      positionInPackage: 1,
      totalInPackage: totalPieces,
      clientName: order?.clientName || '',
      clientPhone: order?.clientPhone || '',
      deliveryAddress: order?.deliveryAddress || '',
      // Usar datos del taller que vienen directamente de la orden (ya incluidos por el componente)
      // NO usar 'Sin taller asignado' aquí - dejar vacío para que generatePackageLabelHtml maneje el fallback
      workshopName: order?.workshopName || '',
      workshopAddress: order?.workshopAddress || '',
      workshopPhone: order?.workshopPhone || '',
      workshopCity: order?.workshopCity || '',
      workshopDepartment: order?.workshopDepartment || '',
      // Información del proveedor
      providerPhone: this.getCompanyWhatsApp(order?.clienteUY)
    };

    // Crear SparePartLabelInfo a partir de spareParts si están disponibles
    let allLabels: SparePartLabelInfo[] | undefined = undefined;
    if (spareParts && spareParts.length > 0) {
      allLabels = spareParts.map((sp, index) => ({
        sparePartId: sp.sparePartId || 0,
        sparePartName: sp.sparePartName || sp.name || 'N/A',
        sparePartNameEs: sp.sparePartNameEs || '',
        productCode: sp.productCode || sp.code || '',
        packageNumber: packageInfo.packageNumber,
        orderNumber: packageInfo.orderNumber,
        siniestro: order?.siniestro || 'N/A',
        clienteUY: order?.clienteUY || 'AUTOCOMERCIAL',
        positionInPackage: index + 1,
        totalInPackage: totalPieces,
        clientName: order?.clientName || '',
        clientPhone: order?.clientPhone || '',
        deliveryAddress: order?.deliveryAddress || '',
        workshopName: packageLabel.workshopName,
        workshopAddress: packageLabel.workshopAddress,
        workshopPhone: packageLabel.workshopPhone,
        workshopCity: packageLabel.workshopCity,
        workshopDepartment: packageLabel.workshopDepartment
      }));
    }

    // Con la nueva altura de 150mm, podemos mostrar más repuestos
    const MAX_SPARE_PARTS_IN_LABEL = 40; // Aumentado a 40 repuestos gracias al espacio extra
    let limitedLabels = allLabels;
    let hasMoreParts = false;

    if (allLabels && allLabels.length > MAX_SPARE_PARTS_IN_LABEL) {
      limitedLabels = allLabels.slice(0, MAX_SPARE_PARTS_IN_LABEL);
      hasMoreParts = true;
    }

    const packageHtml = this.generatePackageLabelHtml(order, packageLabel, totalPieces, limitedLabels, order?.workshopId, hasMoreParts);
    const fullHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Etiqueta del Paquete - ${packageInfo.packageNumber}</title>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
        <style>
          @page {
            size: 100mm 150mm;
            margin: 0;
          }
          body {
            font-family: Arial, sans-serif;
            margin: 20px;
            background-color: #f5f5f5;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 100vh;
          }
          .package-label {
            border: 3px solid #000;
            padding: 15px 15px 15px 8px; /* Menos padding izquierdo para mover contenido a la derecha */
            margin: 20px 20px 24px 24px; /* margen inferior e izquierdo extra */
            page-break-inside: avoid;
            width: 400px; /* Vista previa escalada para pantalla */
            height: 600px; /* Vista previa escalada para pantalla (150mm x 4) */
            background-color: white;
            box-shadow: 0 4px 8px rgba(0,0,0,0.2);
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
          }
          /* Header con logo y primera fila de información */
          .package-header {
            display: flex;
            align-items: flex-start;
            margin-bottom: 10px;
            padding-bottom: 10px;
            border-bottom: 1px solid #000;
            gap: 8px;
            padding-left: 8px;
          }

          .logo-section {
            flex: 0 0 auto;
            width: 160px;
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            padding-top: 10px;
          }

          .company-logo {
            max-height: 80px;
            max-width: 160px;
            object-fit: contain;
          }

          .info-section {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            margin-left: 10px;
          }

          .company-name {
            font-size: 11px;
            font-weight: bold;
            margin-bottom: 2px;
            color: #000;
            line-height: 1.2;
          }

          .company-address {
            font-size: 9px;
            margin-bottom: 2px;
            color: #333;
            line-height: 1.2;
          }

          .company-contact {
            font-size: 8px;
            color: #333;
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            gap: 8px;
            line-height: 1.2;
            align-items: center;
          }

          .company-contact span {
            display: flex;
            align-items: center;
            white-space: nowrap;
          }

          /* Header alternativo (para compatibilidad) */
          .label-header {
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 10px;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
          }
          .label-logo {
            max-height: 60px;
            max-width: 200px;
            object-fit: contain;
          }
          .package-title {
            font-size: 18px;
            font-weight: bold;
            text-align: center;
            margin: 10px 0;
            color: #333;
          }
          .package-main-info {
            margin: 10px 0;
            padding-left: 8px; /* Mover información del taller a la derecha */
          }
          .package-main-info:has(.package-row) {
            margin-bottom: 4px;
          }
          .barcode-section + .package-main-info {
            margin-top: 10px;
            border-top: 1px solid #ccc;
            padding-top: 4px;
            position: relative;
            z-index: 1;
          }
          .workshop-title {
            font-size: 13px;
            font-weight: bold;
            color: #000;
            text-align: left;
            margin-top: -4px;
            margin-bottom: 4px;
          }
          .workshop-details {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            padding-left: 15px;
            gap: 4px;
          }
          .workshop-details div {
            font-size: 12px;
            line-height: 1.3;
            text-align: left;
          }
          .package-row {
            display: flex;
            justify-content: space-between;
            gap: 10px;
            margin-bottom: 6px;
            padding: 3px 0;
            border-bottom: 1px solid #f0f0f0;
            margin-left: 0; /* Sin margen izquierdo adicional para aprovechar espacio */
          }
          .package-row:last-child {
            border-bottom: none;
          }
          .package-row div {
            flex: 1;
            font-size: 12px;
            line-height: 1.2;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .package-row div:first-child {
            text-align: left;
          }
          .package-row div:last-child {
            text-align: right;
          }
          .package-row div:only-child {
            text-align: center;
            flex: none;
          }
          /* Sección del código de barras centrada */
          .barcode-section {
            margin: 10px 0;
            text-align: center;
            width: 100%;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            align-items: center; /* Centrar horizontalmente */
          }
          .barcode-container {
            padding: 0;
            border: 1px solid #ccc;
            background-color: #f9f9f9;
            display: flex;
            flex-direction: column; /* Cambiar a columna para apilar código de barras y número */
            justify-content: flex-start; /* Cambiar a flex-start para reducir espacio */
            align-items: center; /* Centrar horizontalmente */
            width: auto; /* Ancho automático para centrar mejor */
            max-width: 100%;
            gap: 0;
          }
          .barcode-container svg.barcode {
            width: 100%;
            max-width: 100%;
            height: 60px;
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            overflow: visible;
            display: block; /* Asegurar que el SVG se muestre como bloque */
          }
          .recipient-info {
            margin-top: 15px;
            padding-top: 10px;
            border-top: 2px solid #000;
          }
          .client-header {
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 10px;
          }
          .client-logo {
            max-height: 40px;
            max-width: 120px;
            margin-right: 10px;
            object-fit: contain;
          }
          .client-company {
            font-size: 16px;
            font-weight: bold;
            color: #666;
          }
          .delivery-info {
            margin-top: 10px;
            padding: 8px;
            background-color: #f0f8ff;
            border-left: 4px solid #007bff;
          }
          .delivery-info strong {
            color: #007bff;
          }
          .workshop-info {
            margin-top: 10px;
            padding: 8px;
            background-color: #fff3cd;
            border-left: 4px solid #ffc107;
          }
          .workshop-info strong {
            color: #856404;
          }
          .spare-parts-section {
            margin-top: 15px;
            padding-top: 10px;
            border-top: 2px solid #000;
            font-size: 10px;
            flex: 1; /* Ocupa todo el espacio disponible */
            display: flex;
            flex-direction: column;
          }
          .spare-parts-note {
            text-align: center;
            font-size: 8px;
            color: #666;
            margin-top: 5px;
            font-style: italic;
          }
          .spare-parts-title {
            text-align: center;
            color: #000;
            font-size: 11px;
            margin-bottom: 8px;
          }
          .spare-parts-grid {
            margin-top: 8px;
            flex: 1; /* Ocupa todo el espacio disponible */
            overflow-y: auto; /* Scroll si hay muchos repuestos */
          }
          .spare-parts-columns {
            display: flex;
            justify-content: space-between;
            gap: 15px;
          }
          .spare-parts-column {
            flex: 1;
            min-width: 0;
          }
          .spare-part-item {
            font-size: 9px;
            line-height: 1.2;
            margin-bottom: 2px;
            padding: 1px 0;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
          .footer-info {
            margin-top: 15px;
            border-top: 2px solid #000;
            padding-top: 10px;
          }

          .footer-row {
            display: flex;
            justify-content: space-between;
            font-size: 11px;
            color: #333;
            font-weight: bold;
          }

          .footer-row div {
            flex: 1;
          }

          .footer-row div:first-child {
            text-align: left;
          }

          .footer-row div:last-child {
            text-align: right;
          }

          .whatsapp-message {
            text-align: center;
            font-size: 10px;
            color:rgb(0, 0, 0);
            font-weight: bold;
            margin-top: 12px;  /* AUMENTADO de 8px a 12px para más separación */
            padding: 2px;
            background-color: #f0f8f0;
            border-radius: 3px;
          }
          @media print {
            body {
              margin: 0;
              background-color: white;
            }
            .package-label {
              box-shadow: none;
              border: 2px solid #000;
              width: 100mm;
              height: 150mm;
              overflow: hidden;
              page-break-inside: avoid;
              page-break-before: avoid;
              page-break-after: avoid;
              display: flex;
              flex-direction: column;
              padding: 15px 15px 15px 8px; /* Menos padding izquierdo para aprovechar espacio */
            }
            .spare-parts-section {
              flex: 1;
              overflow: visible;
            }
            .spare-parts-grid {
              flex: 1;
              overflow: visible;
            }
            .spare-parts-column {
              overflow: visible;
            }
            .spare-parts-note {
              font-size: 7px !important;
              margin-top: 3px !important;
            }
          }
        </style>
      </head>
      <body>
        ${packageHtml}
        <script>
          // Renderizar códigos de barras usando JsBarcode (mismo formato que vista de órdenes)
          if (typeof JsBarcode !== 'undefined') {
            document.querySelectorAll('.barcode').forEach(function(element) {
              const code = element.dataset.code;
              if (code) {
                JsBarcode(element, code, {
                  format: "CODE128",
                  width: 3,
                  height: 60,
                  displayValue: false
                });
              }
            });
          }
        </script>
      </body>
      </html>
    `;

    // Abrir ventana de impresión
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(fullHtml);
      printWindow.document.close();
      printWindow.focus();

      // Esperar un poco para que se cargue el contenido
      setTimeout(() => {
        printWindow.print();
      }, 500);
    }
  }

  /**
   * Abre una ventana de impresión con las etiquetas generadas
   */
  printLabelsWindow(order: any, labels: SparePartLabelInfo[], useSharedNumber: boolean = false, includePackageLabel: boolean = true, explicitWorkshopId?: number): void {
    // Forzar el workshopId correcto
    const finalWorkshopId = explicitWorkshopId || order?.workshopId;

    // Crear una copia del order con el workshopId correcto
    const orderWithCorrectWorkshop = {
      ...order,
      workshopId: finalWorkshopId
    };

    const labelsHtml = this.generateLabelsHtml(orderWithCorrectWorkshop, labels, useSharedNumber, includePackageLabel, finalWorkshopId);

    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Etiquetas de Repuestos - Orden ${order.orderNumber}</title>
          <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              background-color: #f5f5f5;
            }
            .label {
              border: 2px solid #000;
              padding: 8px 10px 8px 10px;
              margin: 5px auto 10px auto; /* Centrar la etiqueta */
              position: relative;
              page-break-inside: avoid;
              width: 8cm; /* Ancho reducido para que quepa completo */
              min-height: 4.5cm;
              display: flex;
              flex-direction: column;
              justify-content: flex-start;
              background-color: white;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
              box-sizing: border-box;
              overflow: visible;
            }
            .label-header {
              display: flex;
              align-items: center;
              justify-content: space-between;
              margin-bottom: 2px;
              height: 50px; /* Altura aumentada de 45px a 50px */
              padding-left: 4px; /* Mover header ligeramente a la derecha */
            }
            .label-logo {
              max-height: 45px; /* Altura aumentada de 40px a 45px */
              max-width: 180px; /* Ancho aumentado de 160px a 180px */
              object-fit: contain;
            }
            .contact-info {
              font-size: 7px;
              text-align: right;
              line-height: 1.0;
            }
            .label-title {
              font-size: 10px;
              font-weight: bold;
              text-align: center;
              margin: 2px 0;
              color: #333;
            }
            .label-info {
              font-size: 10px; /* Tamaño de fuente aumentado de 9px a 10px */
              line-height: 1.2; /* Line-height aumentado para mejor legibilidad */
              margin: 2px 0 4px 0; /* Márgenes reducidos para mover hacia arriba */
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 3px 10px; /* Gap aumentado para mejor separación */
              padding: 0 4px 0 8px; /* Más padding izquierdo para mover contenido a la derecha */
              box-sizing: border-box;
            }
            .label-info div {
              margin-bottom: 1px;
              word-wrap: break-word;
              overflow-wrap: break-word;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .client-info {
              font-size: 7px;
              line-height: 1.0;
              margin: 2px 0;
              background-color: #f8f9fa;
              padding: 2px 2px 2px 6px; /* Más padding izquierdo para mover contenido a la derecha */
              border-radius: 2px;
            }
            .client-info div {
              margin-bottom: 1px;
            }
            .workshop-info {
              font-size: 6px;
              line-height: 1.0;
              margin: 6px 0 8px 0;
              background-color: #e3f2fd;
              padding: 2px 2px 2px 6px; /* Más padding izquierdo para mover contenido a la derecha */
              border-radius: 2px;
              border: 1px solid #2196f3;
            }
            .workshop-info div {
              margin-bottom: 1px;
              color: #1565c0;
            }
            .client-header {
              display: flex;
              align-items: center;
              gap: 3px;
              margin-bottom: 2px;
              padding-bottom: 2px;
              border-bottom: 1px solid #ddd;
            }
            .client-logo {
              max-height: 20px;
              max-width: 60px;
              object-fit: contain;
              margin-right: 5px;
            }
            .client-company {
              font-weight: bold;
              font-size: 8px;
              color: #333;
              text-transform: uppercase;
            }
            .barcode-container {
              text-align: center;
              margin: 2px 0 4px 0; /* Márgenes reducidos para mover hacia arriba */
              padding-left: 4px; /* Mover código de barras ligeramente a la derecha */
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
            }
            .barcode-container svg.barcode {
              width: 100%;
              max-width: 100%;
              height: 60px; /* Altura aumentada de 50px a 60px para aprovechar mejor el espacio */
              display: block;
              margin: 0 auto;
              overflow: visible;
            }
            .barcode-image {
              max-width: 180px;  /* ANCHO MÁXIMO LIMITADO para evitar que se salga */
              max-height: 55px;
              height: auto;
              width: auto;       /* CAMBIADO de 100% a auto para mantener proporciones */
              object-fit: contain;
              display: block;
              margin: 0 auto;    /* Centrado horizontal */
            }
            .barcode-fallback {
              font-family: 'Arial', 'Helvetica', sans-serif;
              font-size: 14px;
              font-weight: bold;
              text-align: center;
              padding: 5px;
              border: 1px solid #000;
              background-color: #ffffff;
              margin: 2px 0;
              letter-spacing: 1px;
              color: #000000;
            }
            .label-number {
              font-size: 9px;
              text-align: center;
              color: #000;
              font-weight: bold;
              margin-top: auto; /* Empuja el elemento al final */
              margin-bottom: 2px; /* Margen inferior reducido */
              padding: 2px 4px;
              box-sizing: border-box;
              word-wrap: break-word;
              overflow-wrap: break-word;
              display: block;
              white-space: nowrap;
              flex-shrink: 0; /* Evita que se comprima */
            }
             .package-label {
               background-color: #f8f9fa;
               border: 3px solid #000;
             }
             .package-title {
               font-size: 11px;
               font-weight: bold;
               text-align: center;
               margin: 2px 0;
               color: #000;
               background-color: #e9ecef;
               padding: 2px;
               border-radius: 2px;
             }
             .third-card {
               background-color: #f8f9fa;
               border: 3px solid #000;
             }
             .package-main-info {
               font-size: 8px;
               line-height: 1.1;
               margin: 3px 0;
             }
             .package-row {
               display: grid;
               grid-template-columns: 1fr 1fr;
               gap: 2px 8px;
               margin-bottom: 2px;
             }
             .recipient-info {
               font-size: 8px;
               line-height: 1.1;
               margin: 3px 0;
             }
             .recipient-info div {
               margin-bottom: 1px;
             }
             .package-separator {
               display: flex;
               align-items: center;
               margin: 3px 0;
               gap: 4px;
             }
             .separator-line {
               flex: 1;
               height: 1px;
               background-color: #000;
             }
             .separator-text {
               font-size: 7px;
               font-weight: bold;
               color: #000;
               white-space: nowrap;
             }
             .package-instructions {
               font-size: 7px;
               line-height: 1.0;
               margin-top: 3px;
             }
             .instructions-title {
               font-weight: bold;
               margin-bottom: 2px;
               color: #495057;
             }
             .instructions-content p {
               margin: 1px 0;
               color: #6c757d;
             }
             .whatsapp-message {
               text-align: center;
               font-size: 6px;
               color: rgb(0, 0, 0);
               font-weight: bold;
               margin-top: 6px;  /* AUMENTADO de 4px a 6px para más separación */
               padding: 2px;
               background-color: #f0f8f0;
               border-radius: 3px;
             }
             @media print {
              body {
                margin: 0;
                background-color: white;
              }
              .label {
                margin: 5px 10px 10px 8px; /* Margen superior reducido para mover etiqueta hacia arriba */
                page-break-inside: avoid;
                box-shadow: none;
                border-width: 1.5px;
                width: 8.5cm !important; /* Ancho aumentado para impresión */
                min-height: 4.5cm !important; /* Altura aumentada para impresión */
                transform: translate(0, 0px) !important; /* Sin desplazamiento vertical en impresión */
              }
            }
          </style>
        </head>
        <body>
          ${labelsHtml}
          <script>
            // Renderizar códigos de barras usando JsBarcode (mismo formato que vista de órdenes)
            if (typeof JsBarcode !== 'undefined') {
              document.querySelectorAll('.barcode').forEach(function(element) {
                const code = element.dataset.code;
                if (code) {
                  JsBarcode(element, code, {
                    format: "CODE128",
                    width: 2,
                    height: 60,
                    displayValue: false
                  });
                }
              });
            }
          </script>
        </body>
        </html>
      `);

      printWindow.document.close();

      setTimeout(() => {
        printWindow.print();
      }, 500);
    }
  }

  /**
   * Genera HTML optimizado para ticketeras térmicas
   */
  private generateThermalPackageLabelHtml(packageLabel: SparePartLabelInfo, spareParts?: any[], printerWidth: '58mm' | '80mm' | '100mm' = '100mm'): string {
    // Para ticketeras térmicas, usar pixels con DPI específico para mejor precisión
    // Fórmula: pixels = (mm * DPI) / 25.4
    // 203 DPI es estándar para la mayoría de ticketeras térmicas
    const getPixelWidth = (mm: number) => Math.round((mm * 203) / 25.4);
    const getPixelHeight = (mm: number) => Math.round((mm * 203) / 25.4);

    let width: string, height: string, pixelWidth: number, pixelHeight: number;
    let headerHeight: string, barcodeHeight: string, contentHeight: string, footerHeight: string;

    if (printerWidth === '58mm') {
      pixelWidth = getPixelWidth(58);
      pixelHeight = getPixelHeight(100);
      width = `${pixelWidth}px`;
      height = `${pixelHeight}px`;
      // Calcular alturas proporcionales (código de barras más compacto)
      headerHeight = `${Math.round(pixelHeight * 0.12)}px`;   // 12% (header)
      barcodeHeight = `${Math.round(pixelHeight * 0.30)}px`;  // 30% (código de barras REDUCIDO)
      contentHeight = `${Math.round(pixelHeight * 0.28)}px`;  // 28% (info taller)
      footerHeight = `${Math.round(pixelHeight * 0.30)}px`;   // 30% (repuestos + footer MÁS ESPACIO)
    } else if (printerWidth === '80mm') {
      pixelWidth = getPixelWidth(80);
      pixelHeight = getPixelHeight(100);
      width = `${pixelWidth}px`;
      height = `${pixelHeight}px`;
      headerHeight = `${Math.round(pixelHeight * 0.12)}px`;
      barcodeHeight = `${Math.round(pixelHeight * 0.30)}px`;
      contentHeight = `${Math.round(pixelHeight * 0.28)}px`;
      footerHeight = `${Math.round(pixelHeight * 0.30)}px`;
    } else { // 100mm ancho x 150mm alto
      pixelWidth = getPixelWidth(100);
      pixelHeight = getPixelHeight(150);
      width = `${pixelWidth}px`;
      height = `${pixelHeight}px`;
      headerHeight = `${Math.round(pixelHeight * 0.12)}px`;   // 12% (header)
      barcodeHeight = `${Math.round(pixelHeight * 0.30)}px`;  // 30% (código barras)
      contentHeight = `${Math.round(pixelHeight * 0.28)}px`;  // 28% (info taller)
      footerHeight = `${Math.round(pixelHeight * 0.30)}px`;   // 30% (repuestos + footer)
    }
    const logoSrc = this.getCompanyLogoPath(packageLabel.clienteUY);

    // Limitar repuestos a mostrar (máximo 8 para ticketera)
    const maxSpareParts = 8;
    const limitedSpareParts = spareParts?.slice(0, maxSpareParts) || [];
    const hasMoreParts = spareParts && spareParts.length > maxSpareParts;

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Etiqueta Paquete - ${packageLabel.packageNumber}</title>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 0;
            background-color: white;
            width: ${width};
            height: ${height};
            min-height: ${height};
            font-size: 20px;
            line-height: 1.3;
            overflow: hidden;
            box-sizing: border-box;
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
            display: flex;
            flex-direction: column;
          }

          .thermal-label {
            width: 100%;
            height: 100%;
            min-height: 100%;
            max-width: ${width};
            max-height: ${height};
            border: none;
            padding: 4px;
            box-sizing: border-box;
            overflow: hidden;
            display: flex;
            flex-direction: column;
          }

          /* Header con logo y primera fila de información */
          .package-header {
            display: flex;
            align-items: flex-start;
            margin-bottom: 15px;
            padding-bottom: 20px;
            border-bottom: 1px solid #000;
            gap: 4px;
            height: ${headerHeight};
          }

          .logo-section {
            flex: 0 0 auto;
            width: 55mm;
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            padding-top: 5mm;
          }

          .company-logo {
            max-height: 70mm;
            max-width: 55mm;
            object-fit: contain;
          }

          .provider-phone {
            position: absolute !important;
            left: 180px !important;  /* AUMENTADO de 150px a 180px para más margen izquierdo */
            top: 70% !important;  /* AUMENTADO de 50% a 70% para más margen superior */
            transform: translateY(-50%) !important;
            font-size: 20px !important;
            margin: 0 !important;
            padding: 0 !important;
            display: flex !important;
            align-items: center !important;
            white-space: nowrap !important;
            color: #333 !important;
            font-weight: normal !important;
          }

          .info-section {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            margin-left: 8px;
          }

          .company-name {
            font-size: 28px;
            font-weight: bold;
            margin-bottom: 4px;
            color: #000;
            line-height: 1.2;
          }

          .company-address {
            font-size: 22px;
            margin-bottom: 4px;
            color: #333;
            line-height: 1.2;
          }

          .company-contact {
            font-size: 22px;
            color: #333;
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            gap: 12px;
            line-height: 1.2;
            align-items: center;
          }

          .company-contact span {
            display: flex;
            align-items: center;
            white-space: nowrap;
          }

          /* Información del taller */
          .package-main-info {
            margin: 0 0;
            height: ${contentHeight};
            flex: 1;
            display: flex;
            flex-direction: column;
          }
          .barcode-section + .package-main-info {
            margin-top: 10px;
            border-top: 2px solid #000;
            padding-top: 10px;
            position: relative;
            z-index: 1;
          }

          .workshop-title {
            font-size: 30px;
            font-weight: bold;
            color: #000;
            text-align: left;
            margin-top: -6px;
            margin-bottom: 6px;
          }

          .workshop-details {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            padding-left: 20px;
            gap: 6px;
          }
          .workshop-details div {
            font-size: 24px;
            line-height: 1.4;
            text-align: left;
          }

          .package-row {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 15px;
            margin-bottom: 8px;
            margin-left: 30px;  /* AUMENTADO margen izquierdo para separar del borde */
            padding: 6px 0;
            border-bottom: 2px solid #f0f0f0;
            font-size: 28px;
            line-height: 1.4;
          }

          .package-row:last-child {
            border-bottom: none;
          }

          .package-row div {
            flex: 1;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .package-row div:first-child {
            text-align: left;
          }

          .package-row div:last-child {
            text-align: right;
          }

          /* Sección del código de barras */
          .barcode-section {
            margin: 0;
            padding: 0;
            height: ${barcodeHeight};
            width: 100%;
            display: flex;
            justify-content: center;
            align-items: center;
          }

          .barcode-container {
            padding: 0;
            border: none;
            background-color: white;
            display: flex;
            flex-direction: column; /* Cambiar a columna para apilar código de barras y número */
            justify-content: flex-start;
            align-items: center;
            margin: 0;
            box-sizing: border-box;
            overflow: hidden;
            width: 100%;
            height: 100%;
            gap: 0;
          }

          .barcode-container svg.barcode {
            width: 100%;
            max-width: 100%;
            height: auto;
            margin: 0;
            padding: 0;
            display: block;
            box-sizing: border-box;
            overflow: visible;
          }

          .barcode-mini {
            width: auto;        /* CAMBIADO de 100% a auto para mantener proporciones */
            height: 100%;
            max-width: 120px;   /* ANCHO MÁXIMO LIMITADO para térmicas */
            max-height: 50mm;
            object-fit: contain;
            display: block;
            margin: 0 auto;
          }

          /* Repuestos del paquete */
          .spare-parts-section {
            margin-top: 0;
            padding-top: 0;
            border-top: 3px solid #000;
            font-size: 22px;
            height: ${footerHeight};
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-start;
          }

          .spare-parts-note {
            text-align: center;
            font-size: 18px;
            color: #666;
            margin-top: 6px;
            font-style: italic;
          }

          .spare-parts-title {
            text-align: center;
            color: #000;
            font-size: 26px;
            margin-bottom: 2px;
            font-weight: bold;
          }

          .spare-parts-grid {
            margin-top: 0;
            align-self: stretch;
          }

          .spare-parts-empty {
            margin-top: 0;
            padding-top: 0;
            align-self: stretch;
          }

          .spare-parts-columns {
            display: flex;
            justify-content: space-between;
            gap: 18px;
          }

          .spare-parts-column {
            flex: 1;
            min-width: 0;
          }

          .spare-part-item {
            font-size: 20px;
            line-height: 1.3;
            margin-bottom: 6px;
            padding: 3px 0;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }

          .whatsapp-message {
            text-align: center;
            font-size: 18px;
            color: #000;
            font-weight: bold;
            margin-top: 8px;  /* CAMBIADO de auto a 8px para más separación consistente */
            padding: 4px;
            background-color: #f0f8f0;
            border-radius: 3px;
          }

          .footer-info {
            margin-top: 0;
            border-top: 3px solid #000;
            padding-top: 4px;
            margin-bottom: 0;
          }

          .footer-row {
            display: flex;
            justify-content: space-between;
            font-size: 24px;
            color: #333;
            font-weight: bold;
          }

          .footer-row div {
            flex: 1;
          }

          .footer-row div:first-child {
            text-align: left;
          }

          .footer-row div:last-child {
            text-align: right;
          }

          @media print {
            body {
              margin: 0 !important;
              padding: 0 !important;
              width: ${width} !important;
              height: ${height} !important;
              max-width: ${width} !important;
              max-height: ${height} !important;
              overflow: hidden !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              color-adjust: exact !important;
              transform: scale(1) !important;
              transform-origin: top left !important;
              orientation: portrait !important;
            }

            .thermal-label {
              width: 100% !important;
              height: 100% !important;
              min-height: 100% !important;
              max-width: ${width} !important;
              max-height: ${height} !important;
              border: none !important;
              padding: 2mm !important;
              margin: 0 !important;
              box-sizing: border-box !important;
              overflow: visible !important;
              page-break-inside: avoid !important;
              page-break-before: avoid !important;
              page-break-after: avoid !important;
              display: flex !important;
              flex-direction: column !important;
              align-items: stretch !important;
            }

            .package-header {
              height: ${headerHeight} !important;
            }

            .barcode-section {
              height: ${barcodeHeight} !important;
            }

            .package-main-info {
              height: ${contentHeight} !important;
            }

            .spare-parts-section {
              height: ${footerHeight} !important;
            }

            /* Evitar cualquier salto de página y transformar */
            * {
              page-break-inside: avoid !important;
              page-break-before: avoid !important;
              page-break-after: avoid !important;
              transform: none !important;
              -webkit-transform: none !important;
            }

            /* Configuración específica para ticketera térmica */
            @page {
              size: ${printerWidth === '100mm' ? '100mm 150mm' : printerWidth === '80mm' ? '80mm 100mm' : '58mm 100mm'} portrait;
              margin: 0;
              padding: 0;
            }

            html, body {
              width: ${pixelWidth}px !important;
              height: ${pixelHeight}px !important;
              min-height: ${pixelHeight}px !important;
              margin: 0 !important;
              padding: 0 !important;
              overflow: hidden !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="thermal-label">
          <!-- 1. Header con logo y datos de la empresa -->
          <div class="package-header">
            <div class="logo-section">
              <img class="company-logo" src="${logoSrc}" onerror="this.src='/assets/images/autocomercialdelnorte_logo.jpg'" alt="Logo Empresa" />
            </div>
            <div class="info-section">
              <div class="company-name"><strong>${this.getCompanyName(packageLabel.clienteUY)}</strong></div>
              ${this.getCompanyAddress(packageLabel.clienteUY) ? `<div class="company-address">Dirección: ${this.getCompanyAddress(packageLabel.clienteUY)}</div>` : ''}
              <div class="company-contact" style="display: flex; flex-direction: column; gap: 4px; align-items: flex-start;">
                <div style="display: flex; align-items: center;">
                  <img src="/assets/images/whatsapp.png" alt="WhatsApp" style="width: 18px; height: 18px; margin-right: 4px; vertical-align: middle; display: inline-block;">
                  ${packageLabel.providerPhone || this.getCompanyWhatsApp(packageLabel.clienteUY)}
                </div>
                <div style="display: flex; align-items: center;">
                  <img src="/assets/images/gmail.png" alt="Email" style="width: 18px; height: 18px; margin-right: 4px; vertical-align: middle; display: inline-block;">
                  ${this.getCompanyEmail(packageLabel.clienteUY)}
                </div>
              </div>
            </div>
          </div>

          <!-- 2. Orden y Siniestro -->
          <div class="package-main-info">
            <div class="package-row" style="flex-wrap: wrap;">
              <div style="word-wrap: break-word; overflow-wrap: break-word; white-space: normal;"><strong>Orden:</strong> ${packageLabel.orderNumber}</div>
              <div style="word-wrap: break-word; overflow-wrap: break-word; white-space: normal;"><strong>Siniestro:</strong> ${packageLabel.siniestro}</div>
            </div>
          </div>

          <!-- 3. Código de barras con número de paquete visible debajo -->
          <div class="barcode-section" style="margin-top: 15px; margin-bottom: 15px;">
            <div class="barcode-container">
              ${this.generateBarcodeSvg(packageLabel.packageNumber)}
              <!-- Número de paquete visible -->
              <div style="text-align: center; font-size: 32px; font-weight: bold; margin-top: 10px; color: #000; width: 100%;">
                ${packageLabel.packageNumber}
              </div>
            </div>
          </div>

          <!-- 4. Datos Del Envio (taller) -->
          <div class="package-main-info" style="padding-left: 0; margin-left: 0;">
            <div class="workshop-title" style="text-align: left; margin-left: 0;"><strong>Datos Del Envio:</strong></div>
            <div class="workshop-details" style="padding-left: 0; margin-left: 0;">
              <div><strong>Nombre:</strong> ${packageLabel.workshopName || 'Sin asignar'}</div>
              <div><strong>Teléfono:</strong> ${packageLabel.workshopPhone || ''}</div>
              <div><strong>Dirección:</strong> ${packageLabel.workshopAddress || ''}</div>
              <div><strong>Ciudad:</strong> ${packageLabel.workshopCity || ''}</div>
              <div><strong>Departamento:</strong> ${packageLabel.workshopDepartment || ''}</div>
            </div>
          </div>

          <!-- 5. Repuestos del paquete -->
          <div class="spare-parts-section" style="padding-left: 0; margin-left: 0;">
            <div class="spare-parts-title" style="text-align: left; margin-left: 0;"><strong>Repuestos:</strong></div>
            ${limitedSpareParts.length > 0 ? `
            <div class="spare-parts-grid">
              <div class="spare-parts-columns">
                <div class="spare-parts-column">
                  ${limitedSpareParts.slice(0, Math.ceil(limitedSpareParts.length / 2)).map((part, index) =>
      `<div class="spare-part-item">Peça ${index + 1}: ${part.sparePartNameEs || part.sparePartName || part.name || 'N/A'}</div>`
    ).join('')}
                </div>
                <div class="spare-parts-column">
                  ${limitedSpareParts.slice(Math.ceil(limitedSpareParts.length / 2)).map((part, index) =>
      `<div class="spare-part-item">Peça ${Math.ceil(limitedSpareParts.length / 2) + index + 1}: ${part.sparePartNameEs || part.sparePartName || part.name || 'N/A'}</div>`
    ).join('')}
                </div>
              </div>
              ${hasMoreParts ? `<div class="spare-parts-note"><em>y ${spareParts!.length - maxSpareParts} repuestos más...</em></div>` : ''}
            </div>
            ` : `
            <div class="spare-parts-empty">
              <div class="spare-part-item">No hay repuestos</div>
            </div>
            `}
          </div>

          <!-- 6. Footer con mensaje de WhatsApp y cantidad de piezas -->
          <div class="whatsapp-message" style="text-align: center; padding: 8px; margin: 8px 0; border: 1px solid #ccc; border-radius: 4px;">
            <strong>Cotizaciones de repuestos para talleres al wpp ${this.getCompanyWhatsApp(packageLabel.clienteUY)}</strong>
          </div>

          <div class="footer-info" style="text-align: center; padding: 8px; margin-top: 8px; border-top: 2px solid #000;">
            <div style="font-size: 24px; font-weight: bold;">
              <strong>Cantidad de Piezas:</strong> ${packageLabel.totalInPackage}
            </div>
          </div>
        </div>
        <script>
          // Renderizar códigos de barras usando JsBarcode (mismo formato que vista de órdenes)
          if (typeof JsBarcode !== 'undefined') {
            document.querySelectorAll('.barcode').forEach(function(element) {
              const code = element.dataset.code;
              if (code) {
                JsBarcode(element, code, {
                  format: "CODE128",
                  width: 3,
                  height: 60,
                  displayValue: false
                });
              }
            });
          }
        </script>
      </body>
      </html>
    `;
  }

  /**
   * Genera el HTML para la etiqueta del paquete
   */
  private generatePackageLabelHtml(order: any, firstLabel: SparePartLabelInfo, totalPieces: number, allLabels?: SparePartLabelInfo[], explicitWorkshopId?: number, hasMoreParts: boolean = false): string {
    const logoSrc = this.getCompanyLogoPath(order?.clienteUY);

    // Determinar el taller correcto usando lógica inteligente
    const finalWorkshopId = this.determineCorrectWorkshopId(order, explicitWorkshopId);

    return `
      <div class="package-label">
        <!-- 1. Header con logo y datos de la empresa -->
        <div class="package-header">
          <div class="logo-section">
            <img class="company-logo" src="${logoSrc}" onerror="this.src='/assets/images/autocomercialdelnorte_logo.jpg'" alt="Logo Empresa" />
          </div>
          <div class="info-section">
            <div class="company-name"><strong>${this.getCompanyName(order?.clienteUY)}</strong></div>
            ${this.getCompanyAddress(order?.clienteUY) ? `<div class="company-address">Dirección: ${this.getCompanyAddress(order?.clienteUY)}</div>` : ''}
            <div class="company-contact" style="display: flex; flex-direction: column; gap: 2px; align-items: flex-start;">
              <div style="display: flex; align-items: center;">
                <img src="/assets/images/whatsapp.png" alt="WhatsApp" style="width: 10px; height: 10px; margin-right: 3px; vertical-align: middle; display: inline-block;">
                ${firstLabel.providerPhone || this.getCompanyWhatsApp(order?.clienteUY)}
              </div>
              <div style="display: flex; align-items: center;">
                <img src="/assets/images/gmail.png" alt="Email" style="width: 10px; height: 10px; margin-right: 3px; vertical-align: middle; display: inline-block;">
                ${this.getCompanyEmail(order?.clienteUY)}
              </div>
            </div>
          </div>
        </div>

        <!-- 2. Orden y Siniestro -->
        <div class="package-main-info">
          <div class="package-row" style="flex-wrap: wrap;">
            <div style="word-wrap: break-word; overflow-wrap: break-word; white-space: normal;"><strong>Orden:</strong> ${order?.orderNumber || firstLabel.orderNumber || 'N/A'}</div>
            <div style="word-wrap: break-word; overflow-wrap: break-word; white-space: normal;"><strong>Siniestro:</strong> ${order?.siniestro || firstLabel.siniestro || 'N/A'}</div>
          </div>
        </div>

        <!-- 3. Código de barras con número de paquete visible debajo -->
        <div class="barcode-section" style="margin-top: 10px; margin-bottom: 10px;">
          <div class="barcode-container">
            ${this.generateBarcodeSvg(firstLabel.packageNumber)}
            <!-- Número de paquete visible y centrado -->
            <div style="text-align: center; font-size: 18px; font-weight: bold; margin-top: 8px; color: #000; width: 100%;">
              ${firstLabel.packageNumber}
            </div>
          </div>
        </div>

        <!-- 4. Datos Del Envio (taller) -->
        <div class="package-main-info" style="padding-left: 0; margin-left: 0;">
          <div class="workshop-title" style="text-align: left; margin-left: 0;"><strong>Datos Del Envio:</strong></div>
          ${finalWorkshopId ? `
          <div class="workshop-details">
            <div><strong>Nombre:</strong> ${order?.workshopName || firstLabel.workshopName || this.getWorkshopDataById(finalWorkshopId).name}</div>
            <div><strong>Teléfono:</strong> ${order?.workshopPhone || firstLabel.workshopPhone || this.getWorkshopDataById(finalWorkshopId).phone}</div>
            <div><strong>Dirección:</strong> ${order?.workshopAddress || firstLabel.workshopAddress || this.getWorkshopDataById(finalWorkshopId).address}</div>
            <div><strong>Ciudad:</strong> ${order?.workshopCity || firstLabel.workshopCity || this.getWorkshopDataById(finalWorkshopId).city}</div>
            <div><strong>Departamento:</strong> ${order?.workshopDepartment || firstLabel.workshopDepartment || this.getWorkshopDataById(finalWorkshopId).department}</div>
          </div>
          ` : `
          <div class="workshop-details">
            <div><strong>Nombre:</strong> Sin taller asignado</div>
            <div><strong>Teléfono:</strong> </div>
            <div><strong>Dirección:</strong> </div>
            <div><strong>Ciudad:</strong> </div>
            <div><strong>Departamento:</strong> </div>
          </div>
          `}
        </div>

        <!-- 5. Repuestos del paquete -->
        <div class="spare-parts-section" style="padding-left: 0; margin-left: 0;">
          <div class="spare-parts-title" style="text-align: left; margin-left: 0;"><strong>Repuestos:</strong></div>
          ${allLabels && allLabels.length > 0 ? `
          <div class="spare-parts-grid">
            ${this.generateSparePartsGridWithNumbers(allLabels)}
          </div>
          ${hasMoreParts ? `<div class="spare-parts-note"><em>y ${totalPieces - allLabels.length} repuestos más...</em></div>` : ''}
          ` : `
          <div class="spare-parts-grid">
            <div class="spare-parts-columns">
              <div class="spare-parts-column">
                <div class="spare-part-item">No hay repuestos</div>
              </div>
            </div>
          </div>
          `}
        </div>

        <!-- 6. Footer con mensaje de WhatsApp y cantidad de piezas -->
        <div class="whatsapp-message" style="text-align: center; padding: 6px; margin: 6px 0; border: 1px solid #ccc; border-radius: 4px;">
          <strong>Cotizaciones de repuestos para talleres al wpp ${this.getCompanyWhatsApp(order?.clienteUY)}</strong>
        </div>

        <div class="footer-info" style="text-align: center; padding: 6px; margin-top: 6px; border-top: 2px solid #000;">
          <div style="font-size: 12px; font-weight: bold;">
            <strong>Cantidad de Piezas:</strong> ${totalPieces}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Genera el HTML para mostrar los nombres de las piezas en 2 columnas con numeración (Peça 1, Peça 2, etc.)
   */
  private generateSparePartsGridWithNumbers(labels: SparePartLabelInfo[]): string {
    if (!labels || labels.length === 0) {
      return '<div class="spare-parts-columns"><div class="spare-parts-column"><div class="spare-part-item">No hay piezas</div></div></div>';
    }

    let gridHtml = '';
    const midPoint = Math.ceil(labels.length / 2);
    const leftColumn = labels.slice(0, midPoint);
    const rightColumn = labels.slice(midPoint);

    gridHtml += '<div class="spare-parts-columns">';

    // Columna izquierda
    gridHtml += '<div class="spare-parts-column">';
    leftColumn.forEach((label, index) => {
      const displayName = label.sparePartNameEs || label.sparePartName || 'N/A';
      gridHtml += `<div class="spare-part-item">Peça ${index + 1}: ${displayName}</div>`;
    });
    gridHtml += '</div>';

    // Columna derecha
    gridHtml += '<div class="spare-parts-column">';
    rightColumn.forEach((label, index) => {
      const displayName = label.sparePartNameEs || label.sparePartName || 'N/A';
      gridHtml += `<div class="spare-part-item">Peça ${midPoint + index + 1}: ${displayName}</div>`;
    });
    gridHtml += '</div>';

    gridHtml += '</div>';

    return gridHtml;
  }

  /**
   * Genera el HTML para mostrar los nombres de las piezas en 2 columnas
   */
  private generateSparePartsGrid(labels: SparePartLabelInfo[]): string {
    if (!labels || labels.length === 0) {
      return '<div class="spare-parts-columns"><div class="spare-parts-column"><div class="spare-part-item">No hay piezas</div></div></div>';
    }

    let gridHtml = '';

    // Dividir las piezas en 2 columnas
    const midPoint = Math.ceil(labels.length / 2);
    const leftColumn = labels.slice(0, midPoint);
    const rightColumn = labels.slice(midPoint);

    gridHtml += '<div class="spare-parts-columns">';

    // Columna izquierda
    gridHtml += '<div class="spare-parts-column">';
    leftColumn.forEach(label => {
      const displayName = label.sparePartNameEs || label.sparePartName || 'N/A';
      gridHtml += `<div class="spare-part-item">• ${displayName}</div>`;
    });
    gridHtml += '</div>';

    // Columna derecha
    gridHtml += '<div class="spare-parts-column">';
    rightColumn.forEach(label => {
      const displayName = label.sparePartNameEs || label.sparePartName || 'N/A';
      gridHtml += `<div class="spare-part-item">• ${displayName}</div>`;
    });
    gridHtml += '</div>';

    gridHtml += '</div>';

    return gridHtml;
  }

  /**
   * Genera el HTML para la tercera tarjeta adicional (Etiqueta del Paquete)
   */
  private generateThirdCardHtml(order: any, labels: SparePartLabelInfo[]): string {
    const logoSrc = this.getCompanyLogoPath(order?.clienteUY);
    const firstLabel = labels[0];

    return `
      <div class="label third-card package-label">
        <div class="label-header">
          <img class="label-logo" src="${logoSrc}" onerror="this.src='/assets/images/autocomercialdelnorte_logo.jpg'" />
          ${this.getContactInfoHtml()}
        </div>
        
        <div class="package-main-info">
          <div class="package-row">
            <div><strong>Nro Orden:</strong> ${order?.orderNumber || firstLabel?.orderNumber || 'N/A'}</div>
            <div><strong>Nro Siniestro:</strong> ${order?.siniestro || firstLabel?.siniestro || 'N/A'}</div>
          </div>
          <div class="package-row">
            <div><strong>Cant Piezas:</strong> ${labels.length}</div>
            <div><strong>Nro Paquete:</strong> ${firstLabel?.packageNumber || 'N/A'}</div>
          </div>
        </div>
        
        <div class="barcode-container">
          ${this.generateBarcodeSvg(firstLabel?.packageNumber || '')}
        </div>

        <div class="recipient-info">
          <div class="client-header">
            <img class="client-logo" src="${logoSrc}" onerror="this.style.display='none'" alt="Logo Cliente" />
            <span class="client-company">${order?.clienteUY || 'AUTOCOMERCIAL'}</span>
          </div>
        </div>
        
        <div class="package-separator">
          <div class="separator-line"></div>
          <div class="separator-text">Etiqueta Cada una etiqueta</div>
          <div class="separator-line"></div>
        </div>
        
        <div class="package-instructions">
          <div class="instructions-title">Instrucciones:</div>
          <div class="instructions-content">
            <p>• Verificar que todas las piezas estén presentes</p>
            <p>• Confirmar que corresponde al paquete indicado</p>
            <p>• Documentar cualquier observación</p>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Obtiene la ruta del logo de la empresa basado en el clienteUY
   * Método público y unificado para todo el sistema
   */
  public getCompanyLogoPath(company: string | undefined): string {
    if (!company) {
      return '/assets/images/autocomercialdelnorte_logo.jpg'; // Logo por defecto
    }

    // Normalizar el nombre del clienteUY para buscar la imagen
    const normalizedCompany = company.toLowerCase().trim();

    // Mapeo directo de clienteUY a logos específicos
    // Si el clienteUY contiene estas palabras clave, usar el logo correspondiente
    if (normalizedCompany.includes('autocomercial')) {
      return '/assets/images/autocomercialdelnorte_logo.jpg';
    }

    if (normalizedCompany.includes('serimport')) {
      return '/assets/images/serimport_logo.jpg';
    }

    // Lista de logos disponibles en la carpeta assets/images/
    const availableLogos = [
      'autocomercialdelnorte_logo.jpg',
      'serimport_logo.jpg'
    ];

    // Buscar por palabras clave del clienteUY en los logos disponibles
    const companyKeywords = normalizedCompany.split(/\s+/);

    for (const keyword of companyKeywords) {
      if (keyword.length > 2) { // Solo buscar palabras de más de 2 caracteres
        const keywordLogo = availableLogos.find(logo =>
          logo.toLowerCase().includes(keyword)
        );
        if (keywordLogo) {
          return `/assets/images/${keywordLogo}`;
        }
      }
    }

    // Si no encuentra ningún logo, usar el logo por defecto
    return '/assets/images/autocomercialdelnorte_logo.jpg';
  }




  /**
   * Obtiene el número de WhatsApp de la empresa basado en el clienteUY
   * Método público y unificado para todo el sistema
   */
  public getCompanyWhatsApp(company: string | undefined): string {
    if (!company) {
      return '098 419 631'; // WhatsApp por defecto (Autocomercial)
    }

    // Normalizar el nombre del clienteUY para buscar la empresa
    const normalizedCompany = company.toLowerCase().trim();

    // Mapeo directo de clienteUY a números de WhatsApp
    if (normalizedCompany.includes('autocomercial')) {
      return '+598 95 765 648';
    }

    if (normalizedCompany.includes('serimport')) {
      return '+598 94 491 670';
    }

    // Si no encuentra coincidencia, usar WhatsApp por defecto (Autocomercial)
    return '+598 95 765 648';
  }

  /**
   * Método público para obtener logo - disponible para todos los componentes
   */
  getLogoForCompany(clienteUY: string | undefined): string {
    return this.getCompanyLogoPath(clienteUY);
  }

  /**
   * Obtiene el nombre de la empresa basado en el clienteUY
   */
  public getCompanyName(clienteUY: string | undefined): string {
    if (!clienteUY) {
      return 'Autocomercial Del Norte LTDA';
    }

    const normalized = clienteUY.toLowerCase().trim();

    if (normalized.includes('serimport')) {
      return 'Serimport LTDA';
    }

    return 'Autocomercial Del Norte LTDA';
  }

  /**
   * Obtiene la dirección de la empresa basado en el clienteUY
   */
  public getCompanyAddress(clienteUY: string | undefined): string {
    if (!clienteUY) {
      return 'Gral. Manuel Oribe 979, Rivera - Uruguay';
    }

    const normalized = clienteUY.toLowerCase().trim();

    if (normalized.includes('serimport')) {
      return ''; // Serimport no muestra dirección en las etiquetas
    }

    return 'Gral. Manuel Oribe 979, Rivera - Uruguay';
  }

  /**
   * Obtiene el email de la empresa basado en el clienteUY
   */
  public getCompanyEmail(clienteUY: string | undefined): string {
    if (!clienteUY) {
      return 'autocomercialdelnorte@gmail.com';
    }

    const normalized = clienteUY.toLowerCase().trim();

    if (normalized.includes('serimport')) {
      return 'serimport.sas.pro@gmail.com';
    }

    return 'autocomercialdelnorte@gmail.com';
  }




  /**
   * Genera el HTML de información de contacto
   */
  private getContactInfoHtml(): string {
    return '';
  }

  /**
   * Determina el taller correcto basándose en la información de la orden
   */
  private determineCorrectWorkshopId(order: any, explicitWorkshopId?: number): number | null {
    // Si hay un workshopId explícito, usarlo
    if (explicitWorkshopId) {
      return explicitWorkshopId;
    }

    // Si la orden tiene workshopId válido, usarlo
    if (order?.workshopId && order.workshopId !== null) {
      return order.workshopId;
    }

    // Si no hay workshopId, retornar null (sin taller asignado)
    return null;
  }

  /**
   * Mapea el ID del taller a los datos correctos del taller
   */
  private getWorkshopDataById(workshopId: number | null | undefined): { name: string; address: string; phone: string; city: string; department: string } {
    if (!workshopId) {
      return {
        name: 'Sin taller asignado',
        address: '',
        phone: '',
        city: '',
        department: ''
      };
    }

    const workshopMap: { [key: number]: { name: string; address: string; phone: string; city: string; department: string } } = {
      1: {
        name: 'Taller Central',
        address: 'Av. 18 de Julio 1234',
        phone: '+598 2901 2345',
        city: 'Montevideo',
        department: 'Montevideo'
      },
      2: {
        name: 'AutoService Norte',
        address: 'Ruta 8 Km 12',
        phone: '+598 2602 3456',
        city: 'Las Piedras',
        department: 'Canelones'
      },
      3: {
        name: 'Mecanica Especializada',
        address: 'Bulevar Artigas 567',
        phone: '+598 2400 4567',
        city: 'Montevideo',
        department: 'Montevideo'
      },
      4: {
        name: 'Taller Express',
        address: 'Av. Italia 890',
        phone: '+598 2487 5678',
        city: 'Montevideo',
        department: 'Montevideo'
      },
      5: {
        name: 'Garage Premium',
        address: 'Constituyente 1234',
        phone: '+598 2203 6789',
        city: 'Montevideo',
        department: 'Montevideo'
      }
    };

    return workshopMap[workshopId] || {
      name: `Taller ID: ${workshopId}`,
      address: '',
      phone: '',
      city: '',
      department: ''
    };
  }

  /**
   * Genera el código de barras SVG para el código del paquete
   * Usa el mismo formato que en la vista de órdenes
   */
  private generateBarcodeSvg(packageCode: string): string {
    try {
      // Usar el mismo formato que en la vista de órdenes: SVG directo con JsBarcode
      // Escapar el packageCode para evitar problemas con caracteres especiales en HTML
      const escapedCode = packageCode.replace(/"/g, '&quot;').replace(/'/g, '&apos;');
      return `<svg class="barcode" data-code="${escapedCode}"></svg>`;
    } catch (error) {
      console.error('Error generando código de barras:', error);
      return `<div class="barcode-fallback">${packageCode}</div>`;
    }
  }
}
